import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, LogIn, UserPlus, Loader2, Mail, Lock, Eye, EyeOff, Chrome } from 'lucide-react';
import { authService, AuthUser } from '../../services/authService';
import { migrationService } from '../../services/migrationService';
import { DOMAIN_TENANTS } from '../../utils/domainTenant';
import { SmartOnboardingWizard } from './SmartOnboardingWizard';
import { WelcomeScreen } from './WelcomeScreen';

interface AuthWrapperProps {
  children: (user: AuthUser) => React.ReactNode;
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const { t } = useTranslation('auth');
  
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout reached, setting loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout

    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      clearTimeout(loadingTimeout);
      setUser(user);
      setLoading(false);
      
      // Check if user just registered
      if (user) {
        const justReg = sessionStorage.getItem('just_registered');
        if (justReg === 'true') {
          setShowOnboarding(true);
          // Don't clear yet - wait for onboarding to complete
        }
        
        // Run migration if user is authenticated and is an admin
        if (user.profile?.role === 'admin') {
          try {
            await migrationService.runMigrationIfNeeded();
          } catch (error) {
            console.error('Migration failed:', error);
          }
        }
      }
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (authMode === 'signin') {
        await authService.signIn(formData.email, formData.password);
      } else {
        await authService.signUp(formData.email, formData.password, formData.name);
        // Mark that user just registered to show onboarding
        sessionStorage.setItem('just_registered', 'true');
      }
    } catch (error: any) {
      setError(error.message || 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-surface to-info flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-txt-secondary font-medium">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-surface to-info flex items-center justify-center p-4">
        <div className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-primary p-8 text-center">
            <h1 className="text-3xl font-bold text-txt-inverted mb-2">Business Hub</h1>
            <p className="text-txt-muted">AI-Powered Sales Intelligence</p>
          </div>

          {/* Form */}
          <div className="p-8">
            <div className="flex space-x-1 bg-bg-surface rounded-lg p-1 mb-6">
              <button
                onClick={() => setAuthMode('signin')}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  authMode === 'signin'
                    ? 'bg-bg-card text-txt shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </button>
              <button
                onClick={() => setAuthMode('signup')}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  authMode === 'signup'
                    ? 'bg-bg-card text-txt shadow-card'
                    : 'text-txt-secondary hover:text-txt'
                }`}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Sign Up
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-danger-soft border border-danger rounded-lg">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {authMode === 'signup' && (
                <div>
                  <label className="block text-sm font-semibold text-txt-secondary mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                      placeholder="John Doe"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                    placeholder="john@company.com"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                    placeholder="Enter your password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-bg-surface rounded transition-colors"
                    disabled={isSubmitting}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-txt-muted" />
                    ) : (
                      <Eye className="w-4 h-4 text-txt-muted" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-primary text-primary-contrast rounded-xl hover:bg-bg-card transition-all shadow-card disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : authMode === 'signin' ? (
                  <LogIn className="w-4 h-4 mr-2" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                {isSubmitting ? 'Please wait...' : authMode === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {/* Or divider */}
            <div className="flex items-center my-4">
              <div className="flex-1 h-px bg-bg-surface-hover" />
              <span className="px-3 text-xs text-txt-muted">OR</span>
              <div className="flex-1 h-px bg-bg-surface-hover" />
            </div>

            {/* Google Sign-In */}
            <button
              onClick={async () => {
                setError(null);
                setIsSubmitting(true);
                try {
                  const result = await authService.signInWithGoogle();
                  // Check if this is a new Google user (they'll have just been created).
                  // Wer per Firmen-Domain einem bestehenden Tenant beitritt, braucht kein Org-Onboarding.
                  const joinedDomainTenant = DOMAIN_TENANTS.some((tenant) => tenant.orgId === result.profile?.orgId);
                  if (result.metadata?.creationTime === result.metadata?.lastSignInTime && !joinedDomainTenant) {
                    sessionStorage.setItem('just_registered', 'true');
                  }
                } catch (e: any) {
                  setError(e.message || 'Google sign-in failed');
                } finally {
                  setIsSubmitting(false);
                }
              }}
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-bg-card border border-strong rounded-xl hover:bg-bg-surface transition-all disabled:opacity-50 flex items-center justify-center"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Chrome className="w-4 h-4 mr-2 text-txt-secondary" />
              )}
              Continue with Google
            </button>

            <div className="mt-6 text-center">
              <p className="text-sm text-txt-secondary">
                {authMode === 'signin' ? "Don't have an account?" : "Already have an account?"}{' '}
                <button
                  onClick={() => {
                    setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
                    setError(null);
                  }}
                  className="text-accent hover:text-accent font-semibold transition-colors"
                  disabled={isSubmitting}
                >
                  {authMode === 'signin' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show onboarding for new users (just registered)
  if (showOnboarding && user) {
    return (
      <SmartOnboardingWizard
        user={{
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || user.profile?.name || 'User'
        }}
        onComplete={() => {
          sessionStorage.removeItem('just_registered');
          setShowOnboarding(false);
          // Reload to reflect new organization setup
          window.location.reload();
        }}
      />
    );
  }

  // Show Welcome Screen for users without organization (not just registered, but no org)
  // This catches users who:
  // - Signed in but never completed onboarding
  // - Were invited but haven't been assigned to an org yet
  // - Have an incomplete profile
  const hasOrganization = user?.profile?.orgId && user.profile.orgId.length > 0;
  
  if (user && !hasOrganization && !showOnboarding) {
    return (
      <WelcomeScreen
        user={{
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || user.profile?.name || 'User'
        }}
        onStartOnboarding={() => {
          // Mark as starting onboarding and show the wizard
          setShowOnboarding(true);
        }}
      />
    );
  }

  return <>{children(user)}</>;
};

export default AuthWrapper;